/**
 * ============================================
 * FEATURE: Dreamy Bamboo enhanced search experience
 * ============================================
 * Experiment ID : Direct implementation
 * Platform      : Shopify theme (Hyper 1.3.3)
 * Variant       : Enhanced search
 * Primary metric: Search usage and search-result click-through
 * Launched      : 2026-08-25
 *
 * ROLLBACK INSTRUCTIONS:
 * Remove the search-experience.js script tag and enhanced-search markup from
 * snippets/predictive-search.liquid. Recent terms are browser-local only.
 *
 * GTM events fired:
 *   cro_variant_impression
 *   dreamy_search_open
 *   dreamy_search_submit
 *   dreamy_search_suggestion_click
 *   dreamy_search_result_click
 *   dreamy_search_trending_product_click
 *   dreamy_search_recent_click
 *   dreamy_search_recent_clear
 * ============================================
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'dreamy_bamboo_recent_searches';
  var MAX_RECENT_SEARCHES = 5;

  function track(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data || {}));
  }

  function fillTemplate(template, fallback, term) {
    var text = template || fallback;
    return text.indexOf('__TERM__') === -1 ? text + ' ' + term : text.split('__TERM__').join(term);
  }

  function parseTerms(value) {
    if (!value) return [];

    try {
      var terms = JSON.parse(value);
      return Array.isArray(terms)
        ? terms.map(function (term) { return String(term).trim(); }).filter(Boolean)
        : [];
    } catch (error) {
      return [];
    }
  }

  function getRecentSearches() {
    try {
      var value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter(function (term) { return typeof term === 'string'; }) : [];
    } catch (error) {
      return [];
    }
  }

  function setRecentSearches(terms) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(terms.slice(0, MAX_RECENT_SEARCHES)));
    } catch (error) {
      // Storage can be unavailable in private browsing. Search still works normally.
    }
  }

  function saveRecentSearch(term) {
    var cleanedTerm = String(term || '').trim();
    if (!cleanedTerm) return;

    var normalizedTerm = cleanedTerm.toLocaleLowerCase();
    var terms = getRecentSearches().filter(function (existingTerm) {
      return existingTerm.toLocaleLowerCase() !== normalizedTerm;
    });

    terms.unshift(cleanedTerm);
    setRecentSearches(terms);
  }

  function buildSearchUrl(form, term) {
    try {
      var url = new URL(form.action, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('q', term);
      return url.toString();
    } catch (error) {
      return '/search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(term);
    }
  }

  function renderRecentSearches(component) {
    var section = component.querySelector('[data-db-recent-section]');
    var list = component.querySelector('[data-db-recent-list]');
    var form = component.querySelector('form[role="search"]');
    if (!section || !list || !form) return;

    var terms = getRecentSearches();
    list.replaceChildren();
    section.hidden = terms.length === 0;

    terms.forEach(function (term) {
      var item = document.createElement('li');
      item.className = 'db-search__recent-item';

      var link = document.createElement('a');
      link.className = 'db-search__recent-link';
      link.href = buildSearchUrl(form, term);
      link.dataset.dbRecentTerm = term;
      link.textContent = term;

      var removeButton = document.createElement('button');
      removeButton.className = 'db-search__recent-remove';
      removeButton.type = 'button';
      removeButton.dataset.dbRecentRemove = term;
      removeButton.setAttribute(
        'aria-label',
        fillTemplate(component.dataset.dbRecentRemoveLabel, 'Remove __TERM__ from recent searches', term)
      );
      removeButton.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 4l12 12M16 4L4 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

      item.append(link, removeButton);
      list.appendChild(item);
    });
  }

  function removeRecentSearch(component, term) {
    var normalizedTerm = String(term || '').toLocaleLowerCase();
    var terms = getRecentSearches().filter(function (existingTerm) {
      return existingTerm.toLocaleLowerCase() !== normalizedTerm;
    });
    setRecentSearches(terms);
    renderRecentSearches(component);
  }

  function initPlaceholderRotation(component, input) {
    var terms = parseTerms(component.dataset.dbRotatingTerms);
    if (terms.length < 2) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    var termIndex = 0;
    var rotationDelay = Number(component.dataset.dbRotationDelay) || 2800;

    window.setInterval(function () {
      if (input.value || document.hidden) return;

      input.classList.add('is-rotating');
      window.setTimeout(function () {
        termIndex = (termIndex + 1) % terms.length;
        input.placeholder = fillTemplate(
          component.dataset.dbPlaceholderTemplate,
          'Search __TERM__',
          terms[termIndex].toLocaleLowerCase()
        );
        input.classList.remove('is-rotating');
      }, 170);
    }, rotationDelay);
  }

  function keepEmptySearchOpen(component, input) {
    if (input.value.trim() || document.activeElement !== input) return;

    component.removeAttribute('results');
    component.classList.add('predictive-search-open');
    document.body.classList.add('search-open');

    var content = component.querySelector('.search__content');
    if (content) content.classList.remove('hidden');
  }

  function initSearchExperience(component) {
    if (component.dataset.dbSearchInitialized === 'true') return;

    var input = component.querySelector('input[type="search"]');
    var form = component.querySelector('form[role="search"]');
    var resetButton = component.querySelector('button[type="reset"]');
    if (!input || !form) return;

    component.dataset.dbSearchInitialized = 'true';
    renderRecentSearches(component);
    initPlaceholderRotation(component, input);

    track('cro_variant_impression', {
      test_name: 'dreamy_bamboo_search_experience',
      experiment_id: 'direct_implementation',
      variant: 'enhanced_search'
    });

    var hasTrackedOpen = false;
    input.addEventListener('focus', function () {
      if (!hasTrackedOpen) {
        hasTrackedOpen = true;
        track('dreamy_search_open', { placement: 'header' });
      }
    });

    input.addEventListener('input', function () {
      window.setTimeout(function () { keepEmptySearchOpen(component, input); }, 340);
    });

    if (resetButton) {
      resetButton.addEventListener('click', function () {
        window.setTimeout(function () { keepEmptySearchOpen(component, input); });
      });
    }

    form.addEventListener('submit', function () {
      var term = input.value.trim();
      if (!term) return;
      saveRecentSearch(term);
      track('dreamy_search_submit', { search_term: term, placement: 'header' });
    });

    component.addEventListener('click', function (event) {
      var suggestion = event.target.closest('[data-db-search-term]');
      if (suggestion && component.contains(suggestion)) {
        var suggestionTerm = suggestion.dataset.dbSearchTerm || '';
        saveRecentSearch(suggestionTerm);
        track('dreamy_search_suggestion_click', {
          search_term: suggestionTerm,
          suggestion_type: suggestion.dataset.dbSuggestionType || 'popular'
        });
        return;
      }

      var recentLink = event.target.closest('[data-db-recent-term]');
      if (recentLink && component.contains(recentLink)) {
        var recentTerm = recentLink.dataset.dbRecentTerm || '';
        saveRecentSearch(recentTerm);
        track('dreamy_search_recent_click', { search_term: recentTerm });
        return;
      }

      var removeButton = event.target.closest('[data-db-recent-remove]');
      if (removeButton && component.contains(removeButton)) {
        removeRecentSearch(component, removeButton.dataset.dbRecentRemove);
        return;
      }

      var clearButton = event.target.closest('[data-db-recent-clear]');
      if (clearButton && component.contains(clearButton)) {
        setRecentSearches([]);
        renderRecentSearches(component);
        track('dreamy_search_recent_clear');
        return;
      }

      var resultLink = event.target.closest('[data-db-result-product]');
      if (resultLink && component.contains(resultLink)) {
        var activeQuery = input.value.trim();
        saveRecentSearch(activeQuery);
        track('dreamy_search_result_click', {
          search_term: activeQuery,
          product_title: resultLink.dataset.dbResultTitle || '',
          product_url: resultLink.href,
          result_position: Number(resultLink.dataset.dbResultPosition) || null
        });
        return;
      }

      var productLink = event.target.closest('.db-search__trending-grid .product-card a[href]');
      if (productLink && component.contains(productLink)) {
        track('dreamy_search_trending_product_click', { product_url: productLink.href });
      }
    });
  }

  function initAll() {
    document.querySelectorAll('predictive-search.db-search').forEach(initSearchExperience);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();